import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import {
  updateWithOptimisticLock,
  OptimisticLockConflictError,
} from "@/lib/optimistic-lock";
import {
  amountToPct,
  effectiveInvoiceTerms,
  insertAdHocInterim,
  sumTermPct,
  type InvoiceTerm,
} from "@/lib/invoice-plan";

/**
 * VIB-2836: skriving av faktureringsplanen (`projects.invoice_terms`).
 *
 * `startup_invoice_pct` skrives alltid sammen med planen (= første ledd), fordi
 * det er feltet create-invoice og innboksen faktisk leser. Endringen logges av
 * DB-triggeren `trg_log_project_invoice_terms_change` (gammel + ny plan, aktør
 * fra sesjonen) — derfor skjer skrivingen her i klienten, ikke i en edge
 * function der aktøren ville vært NULL.
 */
function planPatch(terms: InvoiceTerm[]) {
  return { invoice_terms: terms, startup_invoice_pct: terms[0].pct };
}

interface UpdatePlanArgs {
  projectId: string;
  terms: InvoiceTerm[];
  /** VIB-757: optimistic lock på projects. */
  expectedUpdatedAt: string;
}

/** Lagre en redigert plan fra Detaljer. Validering skjer i kallet (validateInvoicePlanEdit). */
export function useUpdateInvoicePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, terms, expectedUpdatedAt }: UpdatePlanArgs) => {
      await updateWithOptimisticLock("projects", projectId, planPatch(terms), expectedUpdatedAt);
    },
    onSuccess: () => {
      toast.success("Faktureringsplanen er lagret");
    },
    onError: (err) => {
      if (err instanceof OptimisticLockConflictError) {
        toast.error("En annen bruker har endret prosjektet. Last inn på nytt og prøv igjen.");
      } else {
        toast.error(`Kunne ikke lagre planen: ${(err as Error).message}`);
      }
    },
    onSettled: async (_d, _e, { projectId }) => {
      await invalidateFor(qc, "projects", { projectId });
    },
  });
}

interface AdHocInterimArgs {
  projectId: string;
  /** 0-basert posisjon blant mellomleddene (samme som fakturaens milestone_index). */
  milestoneIndex: number;
  amountExclVat: number;
}

/**
 * Legg en nettopp opprettet ad hoc-mellomfaktura inn i planen som et fakturert
 * ledd. Leser prosjektet på nytt (create-invoice kan ha endret `updated_at` via
 * triggere) og skriver med optimistic lock mot den ferske verdien.
 *
 * Prosenten kappes til det som står åpent i planen: har tidligere fakturaer
 * avveket fra planen, er Gjenstående fortsatt fasit for beløpene, og planen
 * skal bare aldri summere over 100 %.
 */
export async function addAdHocInterimToPlan({
  projectId,
  milestoneIndex,
  amountExclVat,
}: AdHocInterimArgs): Promise<void> {
  const { data, error } = await supabase
    .from("projects")
    .select("invoice_terms, startup_invoice_pct, total_amount, updated_at")
    .eq("id", projectId)
    .single();
  if (error || !data) throw new Error(error?.message ?? "Fant ikke prosjektet");

  const terms = effectiveInvoiceTerms(data);
  // Etter de allerede fakturerte leddene, men alltid før sluttleddet.
  const position = Math.min(milestoneIndex + 1, terms.length - 1);
  const openPct = sumTermPct(terms.slice(position));
  const pct = Math.min(amountToPct(amountExclVat, Number(data.total_amount ?? 0)), openPct);
  const next = insertAdHocInterim(terms, position, pct);
  await updateWithOptimisticLock("projects", projectId, planPatch(next), data.updated_at);
}
